import type { PortalProfile } from '@/api/generated/types.gen';

import type { CurrentUser, Membership } from './auth-context';
import { isActivistStaffKind, organizationRoleFromBackend, ROLES } from './roles';

/**
 * The one way a server reply about the person becomes the person the portal
 * shows (portal-session-rules, design Decision 7, I8). Used at sign-in, for
 * /me on a page load, and for the person a renewal describes, so the three
 * cannot drift apart.
 *
 * - `user`: the person, still in the organization that is open (or, for
 *   Activist staff, in none), with the role they hold there now.
 * - `lost`: the open organization is not among theirs - removed from it, or
 *   none chosen yet at sign-in. `remaining` is what they can choose from, and
 *   `userIn` builds the person in the one chosen.
 * - `unknownRole`: a role this build does not recognise. Nothing is guessed:
 *   at sign-in the attempt stops, and on a reload the tab keeps what it holds.
 */
export type ProfileOutcome =
  | { kind: 'user'; user: CurrentUser }
  | {
      kind: 'lost';
      remaining: Membership[];
      userIn: (chosen: Membership) => CurrentUser;
    }
  | { kind: 'unknownRole' };

export function toCurrentUser(
  profile: PortalProfile,
  openOrganizationId: string | null,
): ProfileOutcome {
  const memberships: Membership[] = [];
  for (const organization of profile.organizations ?? []) {
    const role = organizationRoleFromBackend(organization.role);
    if (!role) return { kind: 'unknownRole' };
    memberships.push({
      id: organization.id,
      name: organization.name,
      slug: organization.slug,
      status: organization.status,
      role,
    });
  }

  const person = { id: profile.id, name: profile.full_name, email: profile.email };

  // Staff belong to nowhere, and their role is the same wherever they are.
  if (isActivistStaffKind(profile.kind)) {
    return {
      kind: 'user',
      user: {
        ...person,
        role: ROLES.ACTIVIST_STAFF,
        organizations: memberships,
        organizationId: null,
        organizationName: null,
      },
    };
  }

  const userIn = (chosen: Membership): CurrentUser => ({
    ...person,
    // Their role travels with the organization.
    role: chosen.role,
    organizations: memberships,
    organizationId: chosen.id,
    organizationName: chosen.name,
  });

  const open = memberships.find((membership) => membership.id === openOrganizationId);
  if (open) return { kind: 'user', user: userIn(open) };
  return { kind: 'lost', remaining: memberships, userIn };
}
